import axios from "axios";
import { handleLogoutTokenExpired } from "../../utils/utilityFunctions";

const API = axios.create({
  baseURL: "/api/organization",
});

const UserAPI = axios.create({
  baseURL: "/api/user",
});

// developer api
const DevAPI = axios.create({
  baseURL: "/api/developer",
});

// get organization
const getOrganization = async (id) => {
  // input validation
  if (!id || id.trim() === "") {
    throw new Error("Invalid ID Format.");
  }

  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  try {
    const response = await API.get(`/${id}`, config);
    return response.data;
  } catch (error) {
    // token expiry validation
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    return null; // Handle the error as needed
  }
};

// Get Organization by user
const get_organization_by_user = async (id) => {
  // input validation
  if (!id || id.trim() === "") {
    throw new Error("Invalid User ID Format.");
  }

  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  try {
    const response = await API.get(`/byuser/${id}`, config);
    return response.data;
  } catch (error) {
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    return null; // Handle the error as needed
  }
};

// Add new user
const addNewUser = async (userdata, token) => {
  // input validation
  const {
    first_name,
    last_name,
    email,
    password,
    privilege,
    permissions,
    organization,
  } = userdata;

  if (
    !first_name?.trim() ||
    !last_name?.trim() ||
    !email?.trim() ||
    !password ||
    !privilege ||
    permissions == null ||
    !organization
  ) {
    throw new Error("All user fields are required.");
  }

  // token validation
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  // check current user privilege
  const currentUser = JSON.parse(localStorage.getItem("user"));
  if (!currentUser || !currentUser.organization || !currentUser.privilege) {
    throw new Error("Authentication required with valid organization context.");
  }
  const currentOrgId = currentUser.organization.$oid;
  if (currentOrgId !== organization) {
    throw new Error(
      "You don't have permission to add users to this organization."
    );
  }
  if (!["admin", "owner"].includes(currentUser.privilege)) {
    throw new Error("Insufficient privileges (requires admin or owner).");
  }

  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };
  const user = userdata;

  try {
    const response = await axios.post(
      `/api/user/adduser/${organization}`,
      user,
      config
    );
    return response.data; // Successfully created user response
  } catch (error) {
    console.error("Error adding user:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    // Pull the real message from the backend, or fallback to axios message
    throw new Error(
      error.response.data?.status ||
        error.response.data?.message ||
        error.message ||
        error
    );
  }
};

// add multiple users
const addMultipleUsers = async (usersdata, token) => {
  // data extraction
  const data = usersdata.users;
  const org_id = usersdata.organization;

  // input validation
  if (!Array.isArray(data) || data.length === 0) {
    throw new Error("Invalid data format. Expected an array of users.");
  }
  if (!org_id || org_id.trim() === "") {
    throw new Error("Invalid Organization ID Format.");
  }

  // token validation
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  // check current user privilege
  const currentUser = JSON.parse(localStorage.getItem("user"));
  if (!currentUser || !currentUser.organization || !currentUser.privilege) {
    throw new Error("Authentication required with valid organization context.");
  }
  const currentOrgId = currentUser.organization.$oid;
  if (currentOrgId !== org_id) {
    throw new Error(
      "You don't have permission to add users to this organization."
    );
  }
  if (!["admin", "owner"].includes(currentUser.privilege)) {
    throw new Error("Insufficient privileges (requires admin or owner).");
  }

  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await axios.post(
      `/api/user/addBatchUsers/${org_id}`,
      data,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error adding user:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }

    console.log("error", error.response.status, error.response.data?.message);
    // Pull the real message from the backend, or fallback to axios message
    throw new Error(error.response.data?.message || error.message);
  }
};

// Update user
const updateUser = async (data) => {
  try {
    // input validation
    if (!data || !data.user_id || !data.member_email || !data.userData) {
      throw new Error("All fields are required.");
    }

    // token validation
    let token = null;
    if (!token) {
      const __USER__ = JSON.parse(localStorage.getItem("user"));
      token = __USER__.token;
    }

    if (!token) {
      handleLogoutTokenExpired();
    }

    // check current user privilege
    const currentUser = JSON.parse(localStorage.getItem("user"));
    if (!currentUser || !currentUser.organization || !currentUser.privilege) {
      throw new Error(
        "Authentication required with valid organization context."
      );
    }

    if (!["admin", "owner"].includes(currentUser.privilege)) {
      throw new Error("Insufficient privileges (requires admin or owner).");
    }

    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    const response = await axios.post(
      `/api/user/edit_by_email/${data.user_id}/${data.member_email}`,
      { userData: data.userData },
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error updating user:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }

    console.log("error", error.response.status, error.response.data?.message);
    // Pull the real message from the backend, or fallback to axios message
    throw new Error(error.response.data?.message || error.message);
  }
};

//get all users
const getAllUsers = async (data) => {
  try {
    // token validation
    let token = null;
    if (!token) {
      const __USER__ = JSON.parse(localStorage.getItem("user"));
      token = __USER__.token;
    }

    if (!token) {
      handleLogoutTokenExpired();
    }

    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    // input validation
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }

    const response = await UserAPI.post(
      `/get_multiple_users`,
      { users: data },
      config
    );
    if (!response.data || !response.data.users) {
      throw new Error("Failed to retrieve users.");
    }
    return response.data.users;
  } catch (error) {
    console.error("Error updating user:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }

    console.log("error", error.response.status, error.response.data?.message);
    // Pull the real message from the backend, or fallback to axios message
    throw new Error(error.response.data?.message || error.message);
  }
};

// delete user
const deleteUser = async (email, token) => {
  // token validation
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // input validation
  if (!email || typeof email !== "string" || email.trim() === "") {
    throw new Error("Invalid user email format.");
  }

  // check current user privilege
  const currentUser = JSON.parse(localStorage.getItem("user"));
  if (!currentUser || !currentUser.organization || !currentUser.privilege) {
    throw new Error("Authentication required with valid organization context.");
  }

  if (!["admin", "owner"].includes(currentUser.privilege)) {
    throw new Error("Insufficient privileges (requires admin or owner).");
  }

  try {
    const response = await axios.post(
      `/api/user/delete_by_email/${email}`,
      { data: email },
      config
    );
    return response.data;
  } catch (error) {
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    // If response exists, rethrow backend message; otherwise throw generic
    if (error.response) {
      const status = error.response.status;
      const msg = error.response.data?.message || error.message;
      // E.g. 400: "Invalid email format", 404: "No user exists", etc.
      throw new Error(msg);
    }
    throw new Error(error.message);
  }
};

// set openai key
const saveOpenAIKey = async (orgData) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // input validation
  if (!orgData || !orgData.orgId || !orgData.keyData) {
    throw new Error("Invalid data format.");
  }
  if (!orgData.orgId || typeof orgData.orgId !== "string") {
    throw new Error("Invalid organization ID format.");
  }
  if (!orgData.keyData || typeof orgData.keyData !== "object") {
    throw new Error("Invalid key data format.");
  }

  const orgId = orgData.orgId;
  const keyData = orgData.keyData;
  try {
    const response = await API.post(`/set/openai/${orgId}`, keyData, config);
    return response.data;
  } catch (error) {
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    if (error.response) {
      // Prefer `.error` field, then `.message`, then fallback to axios’s own message
      const msg =
        error.response.data?.error ||
        error.response.data?.message ||
        error.message;
      throw new Error(msg);
    }
    throw new Error(error.message);
  }
};

// delete openai key
const deleteOpenAIKey = async (orgData) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // input validation
  if (!orgData || !orgData.orgId) {
    throw new Error("Invalid organization data format.");
  }

  const orgId = orgData.orgId;
  try {
    const response = await API.post(`/delete/openai/${orgId}`, null, config);
    return response.data;
  } catch (error) {
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    if (error.response) {
      const msg =
        error.response.data?.error ||
        error.response.data?.message ||
        error.message;
      throw new Error(msg);
    }
    throw new Error(error.message);
  }
};

// set pinecone key
const savePineconeKey = async (orgData) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // input validation
  if (!orgData || !orgData.orgId || !orgData.keyData) {
    throw new Error("Invalid data format.");
  }
  if (!orgData.orgId || typeof orgData.orgId !== "string") {
    throw new Error("Invalid organization ID format.");
  }
  if (!orgData.keyData || typeof orgData.keyData !== "object") {
    throw new Error("Invalid key data format.");
  }

  const orgId = orgData.orgId;
  const keyData = orgData.keyData;
  try {
    const response = await API.post(
      `/setPineconeKey/${orgId}`,
      keyData,
      config
    );
    return response.data;
  } catch (error) {
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    if (error.response) {
      // Prefer `.error` field, then `.message`, then fallback to axios’s own message
      const msg =
        error.response.data?.error ||
        error.response.data?.message ||
        error.message;
      throw new Error(msg);
    }
    throw new Error(error.message);
  }
};

// delete pinecone key
const deletePineconeKey = async (orgData) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // input validation
  if (!orgData || !orgData.orgId) {
    throw new Error("Invalid organization data format.");
  }

  const orgId = orgData.orgId;
  try {
    const response = await API.post(
      `/delete/PineconeKey/${orgId}`,
      null,
      config
    );
    return response.data;
  } catch (error) {
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    if (error.response) {
      const msg =
        error.response.data?.error ||
        error.response.data?.message ||
        error.message;
      throw new Error(msg);
    }
    throw new Error(error.message);
  }
};

// set anthropic key
const saveAnthropicKey = async (orgData) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // input validation
  if (!orgData || !orgData.orgId || !orgData.keyData) {
    throw new Error("Invalid data format.");
  }
  if (!orgData.orgId || typeof orgData.orgId !== "string") {
    throw new Error("Invalid organization ID format.");
  }
  if (!orgData.keyData || typeof orgData.keyData !== "object") {
    throw new Error("Invalid key data format.");
  }

  const orgId = orgData.orgId;
  const keyData = orgData.keyData;
  try {
    const response = await API.post(`/set/claude/${orgId}`, keyData, config);
    return response.data;
  } catch (error) {
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    if (error.response) {
      // Prefer `.error` field, then `.message`, then fallback to axios’s own message
      const msg =
        error.response.data?.error ||
        error.response.data?.message ||
        error.message;
      throw new Error(msg);
    }
    throw new Error(error.message);
  }
};

// delete anthropic key
const deleteAnthropicKey = async (orgData) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // input validation
  if (!orgData || !orgData.orgId) {
    throw new Error("Invalid organization data format.");
  }

  const orgId = orgData.orgId;
  try {
    const response = await API.post(`/delete/claude/${orgId}`, null, config);
    return response.data;
  } catch (error) {
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    if (error.response) {
      // Prefer `.error` field, then `.message`, then fallback to axios’s own message
      const msg =
        error.response.data?.error ||
        error.response.data?.message ||
        error.message;
      throw new Error(msg);
    }
    throw new Error(error.message);
  }
};

// set gemini key
const saveGeminiKey = async (orgData) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  // input validation
  if (!orgData || !orgData.orgId || !orgData.keyData) {
    throw new Error("Invalid data format.");
  }
  if (!orgData.orgId || typeof orgData.orgId !== "string") {
    throw new Error("Invalid organization ID format.");
  }
  if (!orgData.keyData || typeof orgData.keyData !== "object") {
    throw new Error("Invalid key data format.");
  }

  const orgId = orgData.orgId;
  const keyData = orgData.keyData;
  try {
    const response = await API.post(`/set/gemini/${orgId}`, keyData, config);
    return response.data;
  } catch (error) {
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    if (error.response) {
      // Prefer `.error` field, then `.message`, then fallback to axios’s own message
      const msg =
        error.response.data?.error ||
        error.response.data?.message ||
        error.message;
      throw new Error(msg);
    }
  }
};

// delete gemini key
const deleteGeminiKey = async (orgData) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // input validation
  if (!orgData || !orgData.orgId) {
    throw new Error("Invalid organization data format.");
  }

  const orgId = orgData.orgId;
  try {
    const response = await API.post(`/delete/gemini/${orgId}`, null, config);
    return response.data;
  } catch (error) {
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    if (error.response) {
      // Prefer `.error` field, then `.message`, then fallback to axios’s own message
      const msg =
        error.response.data?.error ||
        error.response.data?.message ||
        error.message;
      throw new Error(msg);
    }
  }
};

// set s3 creds
const saveS3Credentials = async (orgData) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // input validation
  if (!orgData || !orgData.orgId || !orgData.keyData) {
    throw new Error("Invalid data format.");
  }
  if (!orgData.orgId || typeof orgData.orgId !== "string") {
    throw new Error("Invalid organization ID format.");
  }
  if (!orgData.keyData || typeof orgData.keyData !== "object") {
    throw new Error("Invalid key data format.");
  }

  const orgId = orgData.orgId;
  const keyData = orgData.keyData;
  try {
    const response = await API.post(`/cloud/aws/${orgId}`, keyData, config);
    return response.data;
  } catch (error) {
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    if (error.response) {
      // Prefer `.error` field, then `.message`, then fallback to axios’s own message
      const msg =
        error.response.data?.error ||
        error.response.data?.message ||
        error.message;
      throw new Error(msg);
    }
  }
};

// delete s3 creds
const deleteS3Credentials = async (orgData) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // input validation
  if (!orgData || !orgData.orgId) {
    throw new Error("Invalid data format.");
  }

  const orgId = orgData.orgId;
  try {
    const response = await API.post(
      `/delete/cloud_creds/aws/${orgId}`,
      null,
      config
    );
    return response.data;
  } catch (error) {
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    if (error.response) {
      // Prefer `.error` field, then `.message`, then fallback to axios’s own message
      const msg =
        error.response.data?.error ||
        error.response.data?.message ||
        error.message;
      throw new Error(msg);
    }
  }
};

// get org usage
const get_usage = async (org) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // input validation
  if (!org || !org.org_id || !org.usage_id) {
    return "Invalid organization data format.";
  }

  const org_id = org.org_id;
  const usage_id = org.usage_id;

  const response = await API.get(`/usage/${org_id}/${usage_id}`, config);

  if (response.status === 403) {
    handleLogoutTokenExpired();
  }

  return response.data;
};

// create api key
const generate_APIKey = async (data) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // input validation
  if (!data || !data.orgId || !data.bot_id) {
    throw new Error("Invalid data format.");
  }

  const org_id = data.orgId;
  const bot_id = data.bot_id;

  try {
    const response = await DevAPI.get(`/generate/${org_id}/${bot_id}`, config);
    return response.data;
  } catch (error) {
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    if (error.response) {
      const msg =
        error.response.data?.error ||
        error.response.data?.message ||
        error.message;
      throw new Error(msg);
    }
    throw new Error(error.message);
  }
};

// delete api key
const deleteApiKey = async (key) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  // input validation
  if (!key || typeof key !== "string" || key.trim() === "") {
    throw new Error("Invalid API key format.");
  }

  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        apiKey: key, // This sends the API key in the headers
      },
    };
    const response = await DevAPI.delete(`/delete`, config);
    return response.data;
  } catch (error) {
    if (error.response) {
      if (error.response.status === 403) {
        handleLogoutTokenExpired();
      }
      const msg =
        error.response.data?.error ||
        error.response.data?.message ||
        error.message;
      throw new Error(msg);
    }
    throw new Error(error.message);
  }
};

const deleteOrganization = async (org_id) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  // input validation
  if (!org_id || typeof org_id !== "string" || org_id.trim() === "") {
    throw new Error("Invalid organization ID format.");
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // check current user privilege
  const currentUser = JSON.parse(localStorage.getItem("user"));
  if (!currentUser || !currentUser.organization || !currentUser.privilege) {
    throw new Error("Authentication required with valid organization context.");
  }
  if (!["admin", "owner"].includes(currentUser.privilege)) {
    throw new Error("Insufficient privileges to delete organization.");
  }
  if (currentUser.organization.$oid !== org_id) {
    throw new Error("User does not belong to the specified organization.");
  }

  try {
    const response = await API.post(
      `/deleteorganization/${org_id}`,
      {},
      config
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      if (error.response.status === 403) {
        handleLogoutTokenExpired();
      }
      const msg =
        error.response.data?.error ||
        error.response.data?.message ||
        error.message;
      throw new Error(msg);
    }
    throw new Error(error.message);
  }
};

//get groups by organization
const getGroupsByOrg = async (data) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // input validation
  if (!data || !data.org_id || typeof data.org_id !== "string") {
    throw new Error("Invalid organization ID format.");
  }

  if (data.org_id.trim() === "") {
    throw new Error("Organization ID cannot be empty.");
  }

  // check current user privilege
  const currentUser = JSON.parse(localStorage.getItem("user"));
  if (!currentUser || !currentUser.organization) {
    throw new Error("Authentication required with valid organization context.");
  }
  if (currentUser.organization.$oid !== data.org_id) {
    throw new Error("User does not belong to the specified organization.");
  }

  // Fetch groups via organization ID
  try {
    const response = await API.post(
      `/group/get_via_org/${data.org_id}`,
      {},
      config
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      if (error.response.status === 403) {
        handleLogoutTokenExpired();
      }
      const msg =
        error.response.data?.error ||
        error.response.data?.message ||
        error.message;
      throw new Error(msg);
    }
    throw new Error(error.message);
  }
};

//get groups by user
const getGroupsByUser = async (user_id) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  // input validation
  if (!user_id || user_id.trim() === "") {
    throw new Error("Invalid user ID format.");
  }

  // check current user privilege
  const currentUser = JSON.parse(localStorage.getItem("user"));
  if (!currentUser || !currentUser.organization) {
    throw new Error("Authentication required with valid organization context.");
  }

  try {
    const response = await API.get(`/group/get_via_user/${user_id}`, config);
    return response.data;
  } catch (error) {
    if (error.response) {
      if (error.response.status === 403) {
        handleLogoutTokenExpired();
      }
      const msg =
        error.response.data?.error ||
        error.response.data?.message ||
        error.message;
      throw new Error(msg);
    }
    throw new Error(error.message);
  }
};

// create org group
const createOrgGroup = async (data) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // input validation
  if (!data || !data.org_id || typeof data.org_id !== "string") {
    throw new Error("Invalid organization ID format.");
  }

  if (data.org_id.trim() === "") {
    throw new Error("Organization ID cannot be empty.");
  }
  if (!data.name || typeof data.name !== "string") {
    throw new Error("Invalid group name format.");
  }

  if (!data.owner) {
    throw new Error("Invalid owner format.");
  }

  // check current user privilege
  const currentUser = JSON.parse(localStorage.getItem("user"));
  if (!currentUser || !currentUser.organization || !currentUser.privilege) {
    throw new Error("Authentication required with valid organization context.");
  }
  if (currentUser.organization.$oid !== data.org_id) {
    throw new Error("User does not belong to the specified organization.");
  }
  if (!["admin", "owner"].includes(currentUser.privilege)) {
    throw new Error("Insufficient privileges to create organization.");
  }

  const org_id = data.org_id;

  try {
    const response = await API.post(`/group/create/${org_id}`, data, config);

    return response.data;
  } catch (error) {
    if (error.response) {
      if (error.response.status === 403) {
        handleLogoutTokenExpired();
      }
      const msg =
        error.response.data?.error ||
        error.response.data?.message ||
        error.message;
      throw new Error(msg);
    }
    throw new Error(error.message);
  }
};

// delete org group
const deleteOrgGroup = async (group_id) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // input validation
  if (!group_id || typeof group_id !== "string" || group_id.trim() === "") {
    throw new Error("Invalid group ID format.");
  }

  // check current user privilege
  const currentUser = JSON.parse(localStorage.getItem("user"));
  if (!currentUser || !currentUser.organization || !currentUser.privilege) {
    throw new Error("Authentication required with valid organization context.");
  }
  if (!["admin", "owner"].includes(currentUser.privilege)) {
    throw new Error("Insufficient privileges to create organization.");
  }

  try {
    const response = await API.post(`/group/delete/${group_id}`, {}, config);
    return response.data;
  } catch (error) {
    if (error.response) {
      if (error.response.status === 403) {
        handleLogoutTokenExpired();
      }
      const msg =
        error.response.data?.error ||
        error.response.data?.message ||
        error.message;
      throw new Error(msg);
    }
    throw new Error(error.message);
  }
};

// add multiple bots to group
const addMultipleBotsToGroup = async (data) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // input validation
  if (typeof data !== "object" || data === null) {
    throw new Error("Missing payload for adding bots to group.");
  }

  const { group_id, bot_id_list } = data;
  if (!group_id || typeof group_id !== "string" || !group_id.trim()) {
    throw new Error("Invalid or missing group_id.");
  }

  if (!Array.isArray(bot_id_list) || bot_id_list.length === 0) {
    throw new Error("bot_id_list must be a non-empty array.");
  }
  for (const id of bot_id_list) {
    if (typeof id !== "string" || !id.trim()) {
      throw new Error("Each bot_id in bot_id_list must be a non-empty string.");
    }
  }

  try {
    const response = await API.post(
      `/group/add_multiple/bot/${group_id}`,
      data,
      config
    );

    return response.data;
  } catch (error) {
    if (error.response) {
      if (error.response.status === 403) {
        handleLogoutTokenExpired();
      }
      const msg =
        error.response.data?.error ||
        error.response.data?.message ||
        error.message;
      throw new Error(msg);
    }
    throw new Error(error.message);
  }
};

// delete bot from group
const deleteBotFromGroup = async (data) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // input validation
  if (typeof data !== "object" || data === null) {
    throw new Error("Missing payload for adding bots to group.");
  }

  const { group_id, bot_id } = data;
  if (!group_id || typeof group_id !== "string" || !group_id.trim()) {
    throw new Error("Invalid or missing group_id.");
  }

  if (!bot_id || typeof bot_id !== "string" || !bot_id.trim()) {
    throw new Error("Invalid or missing bot_id.");
  }

  try {
    const response = await API.post(
      `/group/remove/bot/${group_id}/${bot_id}`,
      {},
      config
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      if (error.response.status === 403) {
        handleLogoutTokenExpired();
      }
      const msg =
        error.response.data?.error ||
        error.response.data?.message ||
        error.message;
      throw new Error(msg);
    }
    throw new Error(error.message);
  }
};

// add multiple users to group
const addMultipleUsersToGroup = async (data) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // input validation
  if (typeof data !== "object" || data === null) {
    throw new Error("Missing payload for adding bots to group.");
  }

  const { group_id, user_id_list } = data;
  if (!group_id || typeof group_id !== "string" || !group_id.trim()) {
    throw new Error("Invalid or missing group_id.");
  }

  if (!Array.isArray(user_id_list) || user_id_list.length === 0) {
    throw new Error("bot_id_list must be a non-empty array.");
  }
  for (const id of user_id_list) {
    if (typeof id !== "string" || !id.trim()) {
      throw new Error("Each bot_id in bot_id_list must be a non-empty string.");
    }
  }

  try {
    // Send the batch request to the backend with the user_id_list
    const response = await API.post(
      `/group/add_multiple/user/${group_id}`,
      { user_id_list }, // Send user_id_list in the body
      config
    );

    return response.data;
  } catch (error) {
    if (error.response) {
      if (error.response.status === 403) {
        handleLogoutTokenExpired();
      }
      const msg =
        error.response.data?.error ||
        error.response.data?.message ||
        error.message;
      throw new Error(msg);
    }
    throw new Error(error.message);
  }
};

// delete user from group
const deleteUserFromGroup = async (data) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  // input validation
  if (typeof data !== "object" || data === null) {
    throw new Error("Missing payload for removing user from group.");
  }
  if (
    !data.group_id ||
    typeof data.group_id !== "string" ||
    !data.group_id.trim()
  ) {
    throw new Error("Invalid or missing group_id.");
  }
  if (
    !data.user_id ||
    typeof data.user_id !== "string" ||
    !data.user_id.trim()
  ) {
    throw new Error("Invalid or missing user_id.");
  }

  const { group_id, user_id } = data;

  try {
    const response = await API.post(
      `/group/remove/user/${group_id}/${user_id}`,
      {},
      config
    );

    return response.data;
  } catch (error) {
    if (error.response) {
      if (error.response.status === 403) {
        handleLogoutTokenExpired();
      }
      const msg =
        error.response.data?.error ||
        error.response.data?.message ||
        error.message;
      throw new Error(msg);
    }
    throw new Error(error.message);
  }
};

// update org llm config
const updateLLMConfig = async (data) => {
  // token validation
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  // input validation
  if (typeof data !== "object" || data === null) {
    throw new Error("Payload is missing or malformed.");
  }
  const { org_id, models } = data;
  if (typeof org_id !== "string" || org_id.trim() === "") {
    throw new Error("Invalid or missing org_id.");
  }
  if (typeof models !== "object" || models === null) {
    throw new Error("`models` must be an object of key→model mappings.");
  }
  const modelKeys = Object.keys(models);
  if (modelKeys.length === 0) {
    throw new Error("`models` cannot be empty.");
  }
  for (const key of modelKeys) {
    const val = models[key];
    if (typeof val !== "string" || val.trim() === "") {
      throw new Error(
        `Invalid model selection for "${key}". Must be a non‐empty string.`
      );
    }
  }

  try {
    // Send the batch request to the backend with the user_id_list
    const response = await API.put(`/set-llm-config/${org_id}`, models, config);

    return response.data;
  } catch (error) {
    if (error.response) {
      if (error.response.status === 403) {
        handleLogoutTokenExpired();
      }
      const msg =
        error.response.data?.error ||
        error.response.data?.message ||
        error.message;
      throw new Error(msg);
    }
    throw new Error(error.message);
  }
};

// get tools by organization
const getToolsByOrg = async (org_id) => {
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  if (!org_id) {
    throw new Error("Invalid data. Please refresh and try again");
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    // Send the batch request to the backend with the user_id_list
    const response = await axios.get(`/api/library/get/org/${org_id}`, config);
    return response.data;
  } catch (error) {
    console.log("Error getting combinations: ", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

// get tools by library id
const getToolById = async (library_id) => {
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  if (!library_id) {
    throw new Error("Invalid data. Please refresh and try again");
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    // Send the batch request to the backend with the user_id_list
    const response = await axios.get(`/api/library/get/${library_id}`, config);
    return response.data;
  } catch (error) {
    console.log("Error getting combinations: ", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

// save tool to library
const saveToolToLibrary = async (data) => {
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  if (
    !data.tool_type ||
    !data.tool_id ||
    !data.name ||
    !data.description ||
    !data.default_combination
  ) {
    throw new Error("Invalid data. Please refresh and try again");
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    // Send the batch request to the backend with the user_id_list
    const response = await axios.post(
      `/api/library/create/${data.tool_type}/${data.tool_id}`,
      {
        name: data.name,
        description: data.description,
        default_combination: data.default_combination,
      },
      config
    );
    return response.data;
  } catch (error) {
    console.log("Error getting combinations: ", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

// Remove tool from library
const removeToolFromLibrary = async (library_id) => {
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  if (!library_id) {
    throw new Error("Invalid data. Please refresh and try again");
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    // Send the batch request to the backend with the user_id_list
    const response = await axios.get(
      `/api/library/remove/${library_id}`,
      config
    );
    return response.data;
  } catch (error) {
    console.log("Error getting combinations: ", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

// Validate mongodb
const validateMongoDB = async (data) => {
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { org_id, mongo_uri } = data;

  // Send the batch request to the backend with the user_id_list
  const response = await API.post(
    `/validate_db/database/${org_id}`,
    { db_uri: mongo_uri },
    config
  );

  return response.data;
};

// Save mongodb
const saveMongoDB = async (data) => {
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { org_id, mongo_uri, database_name } = data;

  // Send the batch request to the backend with the user_id_list
  const response = await API.post(
    `/set_db/database/${org_id}`,
    { db_uri: mongo_uri, database_name },
    config
  );

  return response.data;
};

// delete mongodb
const deleteMongoDb = async (data) => {
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { org_id } = data;

  // Send the batch request to the backend with the user_id_list
  const response = await API.delete(`/del_db/database/${org_id}`, config);

  return response.data;
};

const orgService = {
  getOrganization,
  get_organization_by_user,
  addNewUser,
  updateUser,
  deleteUser,
  getAllUsers,
  addMultipleUsers,
  get_usage,
  saveOpenAIKey,
  savePineconeKey,
  generate_APIKey,
  deleteApiKey,
  deleteOpenAIKey,
  deletePineconeKey,
  deleteOrganization,
  deleteAnthropicKey,
  saveAnthropicKey,
  saveGeminiKey,
  deleteGeminiKey,
  saveS3Credentials,
  deleteS3Credentials,
  getGroupsByOrg,
  getGroupsByUser,
  createOrgGroup,
  deleteOrgGroup,
  deleteBotFromGroup,
  deleteUserFromGroup,
  addMultipleBotsToGroup,
  addMultipleUsersToGroup,
  updateLLMConfig,
  getToolsByOrg,
  getToolById,
  saveToolToLibrary,
  removeToolFromLibrary,
  validateMongoDB,
  saveMongoDB,
  deleteMongoDb,
};

export default orgService;
